/**
 * CheckUserDeviceTakeoverUseCase
 *
 * Detects that another browser claimed the single-device-session while this
 * browser was closed. The live `modified`-only subscription in
 * `SubscribeToUserDeviceChangesUseCase` only catches takeovers that happen
 * WHILE this browser is open; the stale-remote-id-at-startup case is not
 * observable through the subscription (the initial snapshot is delivered as
 * `added`, which Flutter parity requires us to skip, and a concurrent
 * `SetUserDeviceUseCase` write would clobber the remote evidence anyway).
 *
 * Flow:
 *   - read local DEVICE_ID from SecureStorage WITHOUT creating one
 *   - if absent → fresh sign-in (or first-ever visit); no takeover possible
 *   - else read remote `user_device/{userId}` once
 *   - if remote is absent or matches local → no takeover
 *   - if remote differs from local → another browser took over
 *
 * On any Firestore read failure the use case fails open (returns `false`)
 * so a transient outage cannot spuriously sign the user out.
 *
 * No Flutter equivalent: mobile uses stable per-device identifiers
 * (`identifierForVendor` / Android fingerprint), so a re-launching device
 * never finds a foreign id at its own slot. The web port synthesises a
 * fresh per-browser UUID, which makes this startup check necessary.
 */
import { UserDeviceService } from '@/domain/services/userDevice/userDeviceService';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class CheckUserDeviceTakeoverUseCase {
  async execute(userId: string): Promise<boolean> {
    try {
      const localDeviceId = await UserDeviceService.getStoredLocalDeviceId();
      if (!localDeviceId) return false;

      const remoteDeviceId = await UserDeviceService.getRemoteDeviceId(userId);
      if (!remoteDeviceId) return false;

      return remoteDeviceId !== localDeviceId;
    } catch (error) {
      appLogger.error(
        'Failed to check user device takeover',
        'CheckUserDeviceTakeoverUseCase',
        { context: { userId }, error: error as Error },
      );
      return false;
    }
  }
}
